import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { httpsCallable } from 'firebase/functions';
import {
  IconBolt,
  IconBrandStrava,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconDownload,
  IconRoute,
} from '@tabler/icons-react';
import { functions } from '../firebase';
import { getLogoForStyle } from '../utils/styleLogos';
import { getStyleBaseName, groupTemplatesByStyle } from '../components/StylePicker';
import { startStravaConnect } from '../utils/stravaConnect';
import StyleAhaCard from '../components/StyleAhaCard';
import Footer from '../components/Footer';
import MythicMapsLogo from '../assets/logos/80ies_TdF_Logo.png';
import StravaConnectButton from '../assets/strava/btn_strava_connect_with_orange.svg';
import sakurasPreview from '../assets/images/sakuras_maps_example.png';
import './ChallengePage.css';
import './MapGenerationPage.css';
import './EventLandingPage.css';

// CommonJS on purpose (shared with scripts/seo-prerender.js), same import style as /maps.
const marketingSeo = require('../seo/marketingSeo');

const DAY_MS = 24 * 60 * 60 * 1000;

// Stable empty array so the aha card's styleGroups prop keeps identity when unclaimed.
const EMPTY_STYLE_GROUPS = [];

// FAQ: the two event-specific questions on top, then exactly the /maps FAQ (single copy
// source in marketingSeo). The route is dynamic and noindexed, so no JSON-LD here.
const EVENT_FAQS = [
  {
    qKey: 'eventLanding.faqWhoQ',
    q: 'Who can use the event style?',
    aKey: 'eventLanding.faqWhoA',
    a: 'Anyone who opens this event link and connects, while the event window is live. Create your maps during the window and the style is yours to use.',
  },
  {
    qKey: 'eventLanding.faqEntryQ',
    q: 'Do I need to be taking part in the event?',
    aKey: 'eventLanding.faqEntryA',
    a: 'No. The link works for anyone: entrants, supporters, or the simply curious. Any Strava activity with GPS works, and there is no minimum distance.',
  },
  ...marketingSeo.MAPS_FAQS,
];

// Landing page for an event link (/event/:eventId): the /maps landing engine, co-branded.
// Using the link unlocks the event's exclusive style(s) (server-side gating in
// getPromptTemplates, time-boxed to the window) AND grants 5 diamonds to whoever uses it,
// new OR existing. The credits are granted server-side inside claimEvent, once per user
// per event. Partner logo/hero/tagline/brand color come from the event doc (upsertEvent).
// The aha card is event-aware: logged-out it mocks the event's own course (the portrait
// beforeImageUrl/afterImageUrl pair), and after the claim settles the auto-generation
// uses the event style.
function EventLandingPage({ user, onCreditsUpdated = null }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const posthog = usePostHog();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [earnedReward, setEarnedReward] = useState(false);
  // True once the claim attempt finished (or was skipped because the window is closed):
  // the aha card only mounts after this so its generation runs with the unlocked styles
  // and after the +5 landed, never racing the claim.
  const [claimSettled, setClaimSettled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const claimTriedRef = useRef(false);
  const viewedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const getEvent = httpsCallable(functions, 'getEvent');
        const res = await getEvent({ eventId });
        if (!cancelled) { setEvent(res.data); setLoading(false); }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t('eventLanding.loadError', 'This event link is no longer valid.'));
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // The served /event/** fallback ships the generic art title; personalize once loaded.
  useEffect(() => {
    if (event?.name) document.title = `${event.name} | Mythic Maps`;
  }, [event]);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    posthog?.capture('event_landing_viewed', {
      page: 'event_landing',
      event_id: eventId,
      is_authenticated: !!user,
    });
  }, [posthog, eventId, user]);

  // Auto-claim once signed in and the event is active. Whatever the outcome, mark the
  // claim settled so the aha card can mount.
  useEffect(() => {
    if (!user || !event) return;
    if (!event.isActive) { setClaimSettled(true); return; }
    if (claimTriedRef.current) return;
    claimTriedRef.current = true;
    setClaiming(true);
    (async () => {
      try {
        const claim = httpsCallable(functions, 'claimEvent');
        const res = await claim({ eventId });
        setClaimed(true);
        // The +5 lands server-side on first claim; refresh the nav badge to show it.
        const earned = !res.data?.alreadyClaimed;
        setEarnedReward(earned);
        if (earned && onCreditsUpdated) onCreditsUpdated();
      } catch (err) {
        console.error('claimEvent failed:', err);
        setError(err.message || t('eventLanding.claimError', 'We could not unlock this event.'));
      } finally {
        setClaiming(false);
        setClaimSettled(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, event, eventId]);

  // Dedupe 2D/3D variants of the same style by base name; prefer the 3D entry's landscape
  // example image (like the art maps page), falling back to the 2D image when 3D has none.
  const displayStyles = useMemo(() => {
    if (!event?.styles) return [];
    const byBase = new Map();
    event.styles.forEach((style) => {
      const baseName = getStyleBaseName(style.name) || style.name || '';
      const is3D = style.mapInputType === '3d' || (style.name || '').includes('3D');
      const entry = { ...style, baseName, is3D };
      const existing = byBase.get(baseName);
      if (!existing) {
        byBase.set(baseName, entry);
        return;
      }
      const preferred = !existing.is3D && is3D ? entry : existing;
      const other = preferred === existing ? entry : existing;
      if (!preferred.exampleImageUrl && other.exampleImageUrl) {
        preferred.exampleImageUrl = other.exampleImageUrl;
      }
      byBase.set(baseName, preferred);
    });
    return Array.from(byBase.values());
  }, [event]);

  // Style-framed window pill: "Style unlocks {date}" before the window; "Style available
  // today only" / "for N more days" / "until {date}" while live. ("Ends Oct 14" alone read
  // as ambiguous: ends what?)
  const timelineText = useMemo(() => {
    if (!event) return '';
    const nowMs = Date.now();
    const formatDate = (ms) => new Date(ms).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
    if (event.startsAtMs && nowMs < event.startsAtMs) {
      return t('eventLanding.startsOn', 'Style unlocks {{date}}', { date: formatDate(event.startsAtMs) });
    }
    if (event.endsAtMs && nowMs <= event.endsAtMs) {
      const daysLeft = Math.ceil((event.endsAtMs - nowMs) / DAY_MS);
      if (daysLeft <= 1) return t('eventLanding.endsToday', 'Style available today only');
      if (daysLeft <= 7) return t('eventLanding.endsInDays', 'Style available for {{count}} more days', { count: daysLeft });
      return t('eventLanding.endsOn', 'Style available until {{date}}', { date: formatDate(event.endsAtMs) });
    }
    return '';
  }, [event, t, i18n.language]);

  // Availability line closing the offer strip: prose, not a bullet (a window is not a
  // benefit), with the "always free" reassurance constant across window states.
  const offerAvailabilityText = useMemo(() => {
    if (!event) return '';
    const nowMs = Date.now();
    const formatLong = (ms) => new Date(ms).toLocaleDateString(i18n.language, { month: 'long', day: 'numeric' });
    if (event.startsAtMs && nowMs < event.startsAtMs) {
      return t('eventLanding.offerAvailabilityUpcoming', 'The event style unlocks {{date}}. Mythic Maps itself is always free.', { date: formatLong(event.startsAtMs) });
    }
    if (event.endsAtMs && nowMs > event.endsAtMs) {
      return t('eventLanding.offerAvailabilityEnded', 'The event window has ended. Mythic Maps itself is always free.');
    }
    if (event.endsAtMs) {
      return t('eventLanding.offerAvailability', 'The event style is available until {{date}}. Mythic Maps itself is always free.', { date: formatLong(event.endsAtMs) });
    }
    return t('eventLanding.offerAvailabilityOpen', 'Mythic Maps itself is always free.');
  }, [event, t, i18n.language]);

  // Event styles in the shape the aha card expects. Only handed over once the claim
  // succeeded: before that, the event templates are still gated server-side, and the
  // card's any-3D fallback would otherwise pick them anyway.
  const eventStyleGroups = useMemo(() => groupTemplatesByStyle(event?.styles || []), [event]);

  // The event's own course as the logged-out mock pair: the original plain map and the
  // generated event-style art, both portrait, admin-uploaded. Needs both halves; the hero
  // stays a banner-only image.
  const mockOverrides = useMemo(() => (
    event?.beforeImageUrl && event?.afterImageUrl
      ? { before: event.beforeImageUrl, afters: [event.afterImageUrl] }
      : null
  ), [event]);

  const ahaAnalyticsProps = useMemo(() => ({ page: 'event_landing', event_id: eventId }), [eventId]);

  const handleStravaCta = () => {
    posthog?.capture('event_landing_strava_clicked', { page: 'event_landing', event_id: eventId });
    startStravaConnect(`/event/${eventId}`);
  };

  const handleCreateClick = () => {
    posthog?.capture('event_landing_create_clicked', { page: 'event_landing', event_id: eventId });
    navigate('/create');
  };

  if (loading) {
    return (
      <div className="event-landing-page">
        <div className="event-landing-message">{t('eventLanding.loading', 'Loading the event…')}</div>
        <Footer />
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="event-landing-page">
        <div className="event-landing-message event-error">{error}</div>
        <Footer />
      </div>
    );
  }

  const accentStyle = event.brandColor ? { '--event-accent': event.brandColor } : undefined;
  const heroStyle = event.heroImageUrl ? { backgroundImage: `url(${event.heroImageUrl})` } : undefined;
  const featuredStyleName = claimed ? (eventStyleGroups[0]?.name || undefined) : undefined;

  return (
    <div className="event-landing-page" style={accentStyle}>
      <div className="event-hero" style={heroStyle} role="presentation" />

      <div className="event-lockup">
        {event.logoUrl && (
          <>
            <span className="event-lockup-chip">
              <img src={event.logoUrl} alt={event.name} />
            </span>
            <span className="event-lockup-x" aria-hidden="true">×</span>
          </>
        )}
        <span className="event-lockup-chip">
          <img src={MythicMapsLogo} alt="Mythic Maps" />
        </span>
      </div>

      <div className="event-content">
        <h1 className="event-title">{event.name}</h1>
        {event.tagline && <p className="event-tagline">{event.tagline}</p>}
        {event.description && <p className="event-desc">{event.description}</p>}
        {timelineText && <p className="event-urgency">{timelineText}</p>}
      </div>

      {/* The /maps aha engine, event-aware: logged-out it mocks the event course, and
          once the claim settles the auto-generation features the event style. */}
      <div className="section-dark">
        <div className="section-inner">
          {user && claiming && (
            <p className="event-status">{t('eventLanding.claiming', 'Unlocking your styles…')}</p>
          )}
          {user && claimed && (
            <div className="event-claimed">
              <p className="event-claimed-line">
                {t('eventLanding.claimedTitle', 'Unlocked!')}
                {earnedReward ? ` ${t('eventLanding.claimedEarned', 'You earned 5 💎.')}` : ''}
                {' '}
                {t('eventLanding.claimedBody', 'The event styles are now available when you create a map.')}
              </p>
            </div>
          )}
          {error && event && <p className="event-error">{error}</p>}
          {(!user || claimSettled) && (
            <StyleAhaCard
              user={user}
              onStravaConnect={handleStravaCta}
              styleGroups={claimed ? eventStyleGroups : EMPTY_STYLE_GROUPS}
              featuredStyleName={featuredStyleName}
              mockBeforeUrl={mockOverrides ? mockOverrides.before : undefined}
              mockAfterUrls={mockOverrides ? mockOverrides.afters : undefined}
              analyticsSource="event_aha"
              analyticsProps={ahaAnalyticsProps}
            />
          )}
        </div>
      </div>

      {/* Offer strip: the extra benefits on top of the free product. */}
      <div className="section-darker">
        <div className="section-inner">
          <div className="challenge-section">
            <div className="section-header-centered">
              <h2>{t('eventLanding.offerTitle', 'What {{name}} entrants get', { name: event.name })}</h2>
            </div>
            {displayStyles.length > 0 && (
              <div className="event-styles-cards">
                {displayStyles.map((style) => {
                  const logo = getLogoForStyle(style.baseName, style.logoUrl);
                  return (
                    <div key={style.id} className="event-style-preview-card">
                      <div className="event-style-preview-media">
                        {style.exampleImageUrl ? (
                          <img
                            src={style.exampleImageUrl}
                            alt={style.baseName}
                            loading="lazy"
                            className="event-style-preview-img"
                          />
                        ) : (
                          <div className="event-style-preview-placeholder">
                            <IconRoute size={34} />
                          </div>
                        )}
                        {logo && <img src={logo} alt="" className="event-style-preview-logo" />}
                      </div>
                      <div className="event-style-preview-name">{style.baseName}</div>
                    </div>
                  );
                })}
              </div>
            )}
            <ul className="event-offer-lines">
              {displayStyles.length > 0 && (
                <li>
                  <IconCheck size={17} />
                  <span>
                    {t('eventLanding.offerStyles', '{{count}} styles drawn for this event, and released nowhere else', { count: displayStyles.length })}
                  </span>
                </li>
              )}
              <li>
                <IconCheck size={17} />
                <span>
                  {t('eventLanding.offerDiamonds', '5 diamonds on us, enough for 5 art maps. Everyone else earns these through challenges and sharing.')}
                </span>
              </li>
              <li>
                <IconCheck size={17} />
                <span>
                  {t('eventLanding.offerEveryRide', 'Every training ride between now and race day, drawn the same way')}
                </span>
              </li>
            </ul>
            {offerAvailabilityText && <p className="event-offer-availability">{offerAvailabilityText}</p>}
          </div>
        </div>
      </div>

      {/* How it works: replicated from /maps (same classes, assets, and copy keys). */}
      <div className="section-gradient">
        <div className="section-inner">
          <div className="challenge-section">
            <div className="section-header-centered">
              <h2>{t('mapGenPage.howItWorks', 'How it works')}</h2>
            </div>
            <div className="hiw-cards">
              <div className="hiw-card">
                <div className="hiw-visual hiw-visual-connect">
                  <img src={StravaConnectButton} alt="Connect with Strava" className="hiw-strava-btn" />
                </div>
                <div className="hiw-content">
                  <div className="hiw-badge">1</div>
                  <h3>{t('mapGenPage.hiw1Title', 'Connect Strava')}</h3>
                  <p>{t('mapGenPage.hiw1Desc', 'One tap. We never touch your activity data beyond reading the route.')}</p>
                </div>
              </div>

              <div className="hiw-card">
                <div className="hiw-visual hiw-visual-auto">
                  <div className="hiw-auto-mock">
                    <div className="hiw-auto-mock-label">
                      <IconBolt size={16} />
                      <span>{t('mapGenPage.hiw2MockLabel', 'Auto-generation')}</span>
                    </div>
                    <div className="hiw-auto-mock-toggle">
                      <span className="hiw-auto-mock-toggle-on">ON</span>
                      <span className="hiw-auto-mock-switch" aria-hidden="true">
                        <span className="hiw-auto-mock-knob" />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="hiw-content">
                  <div className="hiw-badge">2</div>
                  <h3>{t('mapGenPage.hiw2Title', 'Turn on auto-generation')}</h3>
                  <p>{t('mapGenPage.hiw2Desc', 'Recommended. From this moment on, every Strava activity becomes a Mythic Map.')}</p>
                </div>
              </div>

              <div className="hiw-card">
                <div className="hiw-visual hiw-visual-strava">
                  <div className="hiw-strava-mock">
                    <img
                      src={sakurasPreview}
                      alt=""
                      className="hiw-strava-mock-photo"
                    />
                    <div className="hiw-strava-mock-body">
                      <div className="hiw-strava-mock-row">
                        <div className="hiw-strava-mock-avatar" aria-hidden="true" />
                        <div className="hiw-strava-mock-meta">
                          <span className="hiw-strava-mock-name">{t('mapGenPage.hiw3MockUser', 'You')}</span>
                          <span className="hiw-strava-mock-time">{t('mapGenPage.hiw3MockTime', 'Morning ride · just now')}</span>
                        </div>
                        <IconBrandStrava size={18} className="hiw-strava-mock-logo" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="hiw-content">
                  <div className="hiw-badge">3</div>
                  <h3>{t('mapGenPage.hiw3Title', 'The map lands on your activity')}</h3>
                  <p>{t('mapGenPage.hiw3Desc', 'Auto-upload replaces your Strava cover photo with the artwork, plus the transition video from your Strava map into the Mythic Map.')}</p>
                </div>
              </div>

              <div className="hiw-card">
                <div className="hiw-visual hiw-visual-rewards">
                  <div className="hiw-reward-mock">
                    <div className="hiw-reward-icon">
                      <IconDownload size={24} />
                    </div>
                    <div className="hiw-reward-text">
                      <span className="hiw-reward-title">{t('mapGenPage.hiw4Mock', 'Saved to your devices')}</span>
                      <span className="hiw-reward-subtitle">{t('mapGenPage.hiw4MockSub', 'Profile, Strava, camera roll')}</span>
                    </div>
                  </div>
                </div>
                <div className="hiw-content">
                  <div className="hiw-badge">4</div>
                  <h3>{t('mapGenPage.hiw4Title', 'Keep everything')}</h3>
                  <p>{t('mapGenPage.hiw4Desc', 'Your maps stay on your profile, your Strava, and your camera roll.')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Free to create: the /maps pricing block with the event gift called out. */}
      <div className="section-darker">
        <div className="section-inner">
          <div className="challenge-section">
            <div className="section-header-centered">
              <h2>{t('mapGenPage.pricingTitle', 'Free to create')}</h2>
              <p className="section-description">
                {t('eventLanding.pricingDesc', 'Art maps run on diamonds, and you earn them free from challenges, referrals, and sharing your maps. {{name}} entrants start with 5 on us.', { name: event.name })}
              </p>
            </div>

            <div className="map-gen-stats">
              <div className="map-gen-stat">
                <span className="map-gen-stat-value">$0</span>
                <span className="map-gen-stat-label">{t('eventLanding.statJoin', 'To join, no card needed')}</span>
              </div>
              <div className="map-gen-stat">
                <span className="map-gen-stat-value">40+</span>
                <span className="map-gen-stat-label">{t('mapGenPage.statStyles', 'Styles in production, growing monthly')}</span>
              </div>
              <div className="map-gen-stat">
                <span className="map-gen-stat-value">30k+</span>
                <span className="map-gen-stat-label">{t('eventLanding.statMaps', 'Maps created for athletes like you')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="section-default">
        <div className="section-inner">
          <div className="challenge-section">
            <div className="section-header-centered">
              <h2>{t('mapGenPage.faqTitle', 'Frequently asked questions')}</h2>
            </div>
            <div className="faq-list">
              {EVENT_FAQS.map((faq, index) => (
                <div
                  key={index}
                  className={`faq-item ${openFaq === index ? 'open' : ''}`}
                >
                  <button
                    type="button"
                    className="faq-question"
                    onClick={() => {
                      const isOpening = openFaq !== index;
                      setOpenFaq(isOpening ? index : null);
                      if (isOpening) {
                        posthog?.capture('event_landing_faq_toggled', { faq_index: index, event_id: eventId });
                      }
                    }}
                  >
                    <span>{t(faq.qKey, faq.q)}</span>
                    {openFaq === index ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
                  </button>
                  {openFaq === index && (
                    <div className="faq-answer">
                      <p>{t(faq.aKey, faq.a)}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="section-inner challenge-cta-wrapper">
        <div className="challenge-cta-banner">
          {user ? (
            <button type="button" className="challenge-cta-button" onClick={handleCreateClick}>
              {t('eventLanding.createMap', 'Create a map')}
              <IconChevronRight size={18} />
            </button>
          ) : (
            <button type="button" className="map-gen-strava-cta" onClick={handleStravaCta}>
              <img src={StravaConnectButton} alt={t('eventLanding.connectStrava', 'Connect with Strava')} />
            </button>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default EventLandingPage;
